import Immutable, {Map, List} from 'immutable';

const PAYMENT_CHANNEL_CARD_READER = 'card_reader';
const PAYMENT_CHANNEL_ONLINE = 'online';

const DEFAULT_PAYMENT_CHANNEL = PAYMENT_CHANNEL_CARD_READER;

class ECICode extends waffle.misc.Enumerator {
  valueMap() {
    return {
      // Visa
      "05": {
        "text": "05 - 3DS successful",
        "class": "text-success"
      },
      "06": {
        "text": "06 - Card or bank not enrolled",
        "class": "text-warning"
      },
      "07": {
        "text": "07 - Auth unsucessful or not attempted",
        "class": "text-danger"
      },
      // Mastercard
      "02": {
        "text": "02 - 3DS successful",
        "class": "text-success"
      },
      "01": {
        "text": "01 - Card or bank not enrolled",
        "class": "text-warning"
      },
      "00": {
        "text": "00 - Auth unsucessful or not attempted",
        "class": "text-danger"
      },
    }
  }
}

class FieldsToCopy {
  constructor(isOnlineTransaction = false, amount, time, merchant, terminal, rrn, authCode, maskedCard, transactionUUID, acquirer, acquiringRoute, isoResponseCode, commerceIndicator, eciCode, threeDAuthVersion, xid) {
    this.isOnlineTransaction = isOnlineTransaction;
    this.amount = amount;
    this.time = time;
    this.merchant = merchant;
    this.terminal = terminal;
    this.rrn = rrn;
    this.authCode = authCode;
    this.maskedCard = maskedCard;
    this.transactionUUID = transactionUUID;
    this.acquirer = acquirer;
    this.acquiringRoute = acquiringRoute;
    this.isoResponseCode = isoResponseCode;
    this.commerceIndicator = commerceIndicator;
    this.eciCode = eciCode;
    this.threeDAuthVersion = threeDAuthVersion;
    this.xid = xid;
  }

  getFields() {
    // These are the fields to be included when copying. Order of fields is defined here.
    if (this.orderedFields !== null && this.orderedFields !== undefined) {
      return this.orderedFields;
    }

    let fields = [
      {title: "Amount", text: this.amount},
      {title: "Time", text: this.time},
      {title: "Acquirer", text: this.acquirer},
      {title: "Acquiring Route", text: this.acquiringRoute},
      {title: "Merchant", text: this.merchant},
      {title: "Terminal", text: this.terminal},
      {title: "RRN", text: this.rrn},
      {title: "Auth Code", text: this.authCode},
      {title: "Masked Card", text: this.maskedCard},
      {title: "UUID", text: this.transactionUUID},
      {title: "ISO Response Code", text: this.isoResponseCode},
    ];

    if (this.isOnlineTransaction === true) {
      fields.push({title: "Commerce Indicator", text: this.commerceIndicator});
      fields.push({title: "ECI", text: this.eciCode});
      fields.push({title: "3ds Version", text: this.threeDAuthVersion});
      fields.push({title: "XID", text: this.xid});
    }

    this.orderedFields = fields;

    return this.orderedFields;
  }

  // Returns field ordering information about the data available for copying
  getFieldsInfo() {
    let fieldsInfo = 'Use buttons on the left to copy Clearing Info data';

    let fieldsOrdering = [];

    this.getFields().forEach(field => fieldsOrdering.push(`<dd>${field.title}</dd>`));

    fieldsInfo = fieldsInfo + `<br><br><dl><dt>Fields Order: </dt>${fieldsOrdering.join('')}</dl>`;

    fieldsInfo = `<span id="clearingInfoCopy" style="text-align: left;">${fieldsInfo}</span>`

    return fieldsInfo;
  }

  // Formats fields to be copied as tab-separated
  valuesAsTabDelimited() {
    const outputFields = [];

    this.getFields().forEach(field => outputFields.push(`${field.text}`))

    return outputFields.join('\t');

  }

  // Formats fields to be copied as field name: value, and newline-separated
  fieldNameAndValueAsNewlineSeparated() {
    let fields = this.getFields();

    const outputFields = [];

    fields.forEach(field => outputFields.push(`${field.title}: ${field.text}`));

    return outputFields.join('\n');
  }
}

class TransactionFailureCategory extends waffle.misc.Enumerator {
  valueMap() {
    return {
      "network_issue": {
        "text": "Network Issue",
        "icon": "icon-satellite",
        "class": "text-danger"
      },
      "server_error": {
        "text": "Server Error",
        "icon": "icon-clock",
        "class": "text-danger"
      },
      "merchant_error": {
        "text": "Merchant Error",
        "icon": "icon-shop",
        "class": "text-primary"
      },
      "user_error": {
        "text": "User Error",
        "icon": "icon-staff",
        "class": "text-primary"
      },
      "reader_error": {
        "text": "Reader Error",
        "icon": "icon-cardreader",
        "class": "text-danger"
      },
      "bank_decline": {
        "text": "Bank Decline",
        "icon": "icon-balance",
        "class": "text-warning"
      },
      "configuration_issue": {
        "text": "Configuration Issue",
        "icon": "icon-wrench",
        "class": "text-info"
      },
      "partner_issue": {
        "text": "Partner Issue",
        "icon": "icon-connection-3",
        "class": "text-danger"
      },
      "bug": {
        "text": "Bug",
        "icon": "icon-bug-1",
        "class": "text-danger"
      },
      "aborted": {
        "text": "Aborted",
        "icon": "icon-minus-3",
        "class": "text-warning"
      },
      "unknown": {
        "text": "Unknown",
        "icon": "icon-alert",
        "class": "text-danger"
      },
    }
  }
}

class ClearingInfo extends waffle.base.BaseComponent {
  // Extracts fields that can be copied under "Clearing Info"
  fieldsToCopy() {
    if (this.clearingInfoFields !== null && this.clearingInfoFields !== undefined) {
      return this.clearingInfoFields;
    }

    const fields = new FieldsToCopy();

    const transaction = Immutable.fromJS(this.props.parent);
    const processorTransaction = transaction.get('payworksTransaction', Map());

    fields.amount = transaction.get('displayAmount');
    fields.time = moment(transaction.get('created')).format('YYYY/MM/DD HH:mm:ss Z', 'Africa/Johannesburg');
    fields.merchant = processorTransaction.get('clearingMerchantId');
    fields.terminal = processorTransaction.get('clearingTerminalId');
    fields.rrn = processorTransaction.get('clearingTransactionIdentifier');
    fields.authCode = processorTransaction.get('clearingAuthorizationCode');
    fields.maskedCard = processorTransaction.getIn(['processingInfo', 'card', 'maskedPan']);
    fields.transactionUUID = transaction.get('uuid');
    fields.acquirer = processorTransaction.getIn(['processingInfo', 'clearing', 'acquirer'], '').titleize();
    fields.acquiringRoute = processorTransaction.getIn(['processingInfo', 'clearing', 'acquiringRoute'], '').titleize();

    // Only Load ISO Response Code if it's available
    const isoResponseCode = processorTransaction.getIn(['processingInfo', 'clearing', 'isoResponseCode']);
    if (isoResponseCode) {
      fields.isoResponseCode = isoResponseCode;
    }

    // Add fields specific to online transactions
    const paymentChannel = transaction.getIn(['processingDetails', 'paymentChannel'], DEFAULT_PAYMENT_CHANNEL);
    if (paymentChannel === PAYMENT_CHANNEL_ONLINE) {
      fields.isOnlineTransaction = true;

      const threeDAuthInfo = processorTransaction.getIn(['processingInfo', 'clearing', 'threeDAuth'], Map());

      fields.commerceIndicator = threeDAuthInfo.get('commerceIndicator', '');
      fields.eciCode = threeDAuthInfo.get('eci', '');
      fields.threeDAuthVersion = threeDAuthInfo.get('version', '');
      fields.xid = threeDAuthInfo.get('xid', '');
    }

    this.clearingInfoFields = fields;

    return this.clearingInfoFields;
  }

  render() {
    const transaction = Immutable.fromJS(this.props.parent);
    const processorTransaction = transaction.get('payworksTransaction');

    if (!processorTransaction) {
      return null;
    }

    const merchantId = processorTransaction.get('clearingMerchantId')
    const terminalId = processorTransaction.get('clearingTerminalId')
    const authorizationId = processorTransaction.get('clearingAuthorizationCode');

    const isoResponseCode = processorTransaction.getIn(['processingInfo', 'clearing', 'isoResponseCode']);
    const isoResponseCodeDescription = processorTransaction.getIn(['processingInfo', 'clearing', 'isoResponseCodeDescription']);

    return (<div>
      <span className='badge'>Response code: {isoResponseCode} ({isoResponseCodeDescription})</span>&nbsp;
      <span className='badge'>Merchant ID: {merchantId}</span>&nbsp;
      <span className='badge'>Terminal ID: {terminalId}</span>&nbsp;
      <span className='badge'>RRN: {processorTransaction.get('clearingTransactionIdentifier')}</span>&nbsp;
      <span className='badge'>Auth Code: {authorizationId}</span>&nbsp;
      <a
        href='#'
        data-toggle='tooltip'
        title='Copy fields + data'
        onClick={() => {
          waffle.utils.copyToClipboard(this.fieldsToCopy().fieldNameAndValueAsNewlineSeparated());
          Utils.alert('Clearing Info copied!', 'success');
        }}
      >
        <i className='fa fa-copy fa-fw' />
      </a>&nbsp;
      <a
        href='#'
        data-toggle='tooltip'
        title='Copy data only as tab-separated'
        onClick={() => {
          waffle.utils.copyToClipboard(this.fieldsToCopy().valuesAsTabDelimited());
          Utils.alert('Clearing Info copied as tab separated!', 'success');
        }}
      >
        <i className='fa fa-table fa-fw' />
      </a>&nbsp;
      <span
        data-toggle='tooltip'
        data-html='true'
        title={this.fieldsToCopy().getFieldsInfo()}
      >
        <i className="fa fa-info-circle"></i>
      </span>&nbsp;

    </div>);
  }
}

class OnlineInfo extends waffle.base.BaseComponent {
  render() {
    const transaction = Immutable.fromJS(this.props.parent)

    const processingInfo = transaction.getIn(['payworksTransaction', 'processingInfo'])
    // The `route` is called acquirerRoute in Thrive while this will be called acquiringRoute for CNP via CPS
    const route = processingInfo.hasIn(['clearing', 'acquirerRoute']) ? processingInfo.getIn(['clearing', 'acquirerRoute']) : processingInfo.getIn(['clearing', 'acquiringRoute'])
    const verificationMethod = processingInfo.getIn(['card', 'cardholderVerificationMethod'])
    const eci = processingInfo.getIn(['clearing', 'threeDAuth', 'eci'])

    return (
      <div className='card-information'>
        <span className='tag'>Verification: {verificationMethod}</span>
        { eci ? (
          <span className='tag'><span>ECI:</span> {<ECICode value={eci} />}</span>
        ): null}
        <span className='tag'>Processor: {route}</span>
      </div>
    )
  }
}

class ProcessingDetails extends waffle.base.BaseComponent {
  render() {
    const transaction = Immutable.fromJS(this.props.parent);

    return (<div className="card-information">
      <span>Acquiring bank:</span>
      <span className="text-info">{transaction.getIn(['processingDetails', 'processingBank'], 'not_set').titleize()}</span>
      <span>Acquiring route:</span>
      <span className="text-info">{transaction.getIn(['payworksTransaction', 'processingInfo', 'clearing', 'acquiringRoute'], 'not_set').titleize()}</span>
      <span>Payment channel:</span>
      <span className="text-info">{transaction.getIn(['processingDetails', 'paymentChannel'], DEFAULT_PAYMENT_CHANNEL).titleize()}</span>
      <span>Payment type:</span>
      <span className="text-info">{transaction.get('paymentType', 'charge').humanize()}</span>
    </div>);
  }
}

/**
 * This class returns the GPS co-ordinates of the transaction in  lat&long style
 */
class TransactionLocation extends waffle.base.BaseComponent {
  render() {
    const transaction = Immutable.fromJS(this.props.parent);
    const processorTransaction = transaction.get('payworksTransaction');

    const latitudeVal =  transaction.getIn(['location', "latitude"]);
    const longitudeVal = transaction.getIn(['location', "longitude"]);
    console.log(`Transaction Location: (${latitudeVal}, ${longitudeVal})`);
    const mapLink = `https://maps.google.com/maps?&z=15&mrt=yp&t=k&q=${latitudeVal}+${longitudeVal}`;

    if (!processorTransaction) {
      return null;
    }

    return (
        <div className="card-information">
          <span className="tag">{latitudeVal}Â° S, {longitudeVal}Â° E</span>
          <span className="tag"><a href={mapLink}>View on map</a></span>
        </div>
    );
  }
}

class PaymentDetails extends waffle.base.BaseComponent {
  render() {
    const transaction = Immutable.fromJS(this.props.parent);
    const processorTransaction = transaction.get('payworksTransaction');

    if (!processorTransaction) {
      return null;
    }

    return (
      <div className="card-information">
        <span className="tag">Payment source: {processorTransaction.get('paymentDetailsSource', '').humanize()}</span>
        <span className="tag">Verification type: {processorTransaction.get('paymentDetailsCustomerVerification', '').humanize()}</span>
      </div>
    );
  }
}

class TransactionFailureInformation extends waffle.base.BaseComponent {
  render() {
    const failureInformation = this.props.parent.transactionInfo.failureInformation || {};
    const {failureReason, failureCode, failedOnPaymentStep, secondsOnLastPhase, failureCategory, failureSource} = failureInformation;

    function classification() {
      if (failureCategory) {
        if (failureSource) {
          return `(${failureCategory.humanize()} - ${failureSource.humanize()})`;
        } else {
          return `(${failureCategory.humanize()})`;
        }
      }

      return null;
    }

    function stepInfo() {
      if (failedOnPaymentStep && secondsOnLastPhase) {
        return `[during ${failedOnPaymentStep} for ${secondsOnLastPhase} seconds]`;
      } else if (failedOnPaymentStep) {
        return `[during ${failedOnPaymentStep}]`;
      }

      return null;
    }

    if (failureReason) {
      return (
        <div>
          <span className='text-danger failure-reason'><b>{failureReason}</b></span>&nbsp;
          <span className='failure-code text-info'>{failureCode ? `(${failureCode})` : null}</span>&nbsp;
          <span className='failed-on-step text-info'>{classification()}</span>&nbsp;
          <span className='failed-on-step text-muted'>{stepInfo()}</span>
        </div>
      );
    }

    return (<div></div>);
  }
}

class TransactionPayoutState extends waffle.base.BaseComponent {

  render() {
    const transaction = Immutable.fromJS(this.props.parent);
    const settlementState = transaction.getIn(['inboundSettlement', 'state']);

    if (!['credit_card', 'instant_eft', 'buy_now_pay_later'].includes(transaction.get('paymentMethod'))) {
      return <div className='text-muted'>Transaction is not a settleable type</div>;
    }

    // This settlement state indicates we can actually pay out
    const payout = transaction.get('outboundBusinessPayout');

    if (payout) {
      // We have paid out
      const payoutState = payout.get('state');
      const bankDetails = payout.get("paymentBankInfo", Map()).get('submittedBankDetails', Map());

      const payoutSummary = `Submitted at ${Utils.prettyDate(payout.get('submitted'))} to account ending in ${bankDetails.get('accountNumber', '????').slice(-4)}`;

      let errorMessage = '';
      let color = 'text-info';

      if (payoutState === 'rejected' || payoutState === 'unpaid') {
        color = 'text-danger';
      } else if (payoutState === 'paid') {
        color = 'text-success'
      }

      return (
        <div className={color}>
          <a href={`/settlement/outbound-payouts/business-payouts/${payout.get('uuid')}/`}><b>{payout.get('amount')} Payout {payoutState.titleize()}</b></a>
          &nbsp; : {payoutSummary}
        </div>
      );
    } else {
      // When are we expecting to payout?
      const cantSettleMessage = transaction.getIn(['business', 'cantSettleMessage'])

      if (cantSettleMessage) {
        return (
          <div className="text-danger">Will not pay out: {cantSettleMessage}</div>
        );
      } else {
        const outboundSettlement = transaction.get('outboundSettlement', Map());
        if (outboundSettlement.get('isAvailableForPayout', false)) {
          const allowedPayoutDate = outboundSettlement.get('allowedPayoutDate');
          return (
            <div>
              <span className="text-info">Will be included in first payout after {Utils.prettyDate(allowedPayoutDate)}</span>
            </div>
          );
        } {
          return (
            <div>
              <span className="text-danger">Not available for payout</span>
            </div>
          );
        }
      }

    }

    return <div className='text-danger'>Unknown Payout State</div>
  }
}


class TransactionReconState extends waffle.base.BaseComponent {

  render() {
    const transaction = Immutable.fromJS(this.props.parent);
    const settlementState = transaction.getIn(['inboundSettlement', 'state']);
    const markOffEntryUUID = transaction.getIn(['inboundSettlement', 'markOffEntryUUID']);

    if (!['credit_card', 'instant_eft', 'buy_now_pay_later'].includes(transaction.get('paymentMethod'))) {
      return <div className='text-muted'>Transaction is not a settleable type</div>;
    } else if (settlementState === 'reconned') {
      return <div className='text-info'>Reconned - Money received from bank and reconciled</div>;
    } else if (settlementState === 'in_suspense') {
      return <div className='text-info'>Mark off received and transaction in suspense with bank</div>;
    } else if (markOffEntryUUID) {
      return <div className='text-danger'>Mark off received but state incorrect. Check this manually if settled or not</div>;
    } else if (['pending','reserved','waiting_funds','waiting_transaction_result'].includes(settlementState)) {
      return <div className='text-warning'>Pending - Money not yet received from bank</div>;
    } else if (settlementState === 'aborted') {
      return <div className='text-warning'>Aborted - Not expected to settle from bank</div>;
    }

    return (<div className='text-danger'>Unknown Settlement State</div>)
  }
}

class ReaderInfo extends waffle.base.BaseComponent {
  render() {
    const transaction = Immutable.fromJS(this.props.parent);
    const reader = transaction.get('reader');

    if (!reader) {
      const serialNumber = transaction.getIn([
        'transactionInfo', 'debugging', 'accessory', 'serial']);

      if (serialNumber) {
        return <div>{serialNumber}</div>
      } else {
        return <div className="text-muted">No reader information</div>
      }
    } else {
      return <div>
        <a href={`/logistics/card-readers/${reader.get('uuid')}`}>
          <b>{reader.get('serialNumber')}</b>
        </a>&nbsp;
        <span>{reader.get('make').humanize()} {reader.get('model').humanize()}</span>&nbsp;
        <span className="text-muted">Firmware: {reader.getIn(['readerInfo', 'firmware'])} / Software: {reader.getIn(['readerInfo', 'software'])}</span>
      </div>
    }
  }
}

class HardwareInfo extends waffle.base.BaseComponent {
  render() {
    const transaction = Immutable.fromJS(this.props.parent);
    const device = transaction.get('hardwareDevice');

    if (!device) {
      return <div>No device information</div>
    } else {
      return <div>
        <a href={`/devices/${device.get('uuid')}`}>{device.get('name', `Unnamed ${device.get('model', 'device')}`)}</a>&nbsp;
        <span>{device.get('make')} {device.get('model')}</span>&nbsp;
        <span className="text-muted">{device.getIn(['metadata', 'osName'])} {device.getIn(['metadata', 'osVersion'])}</span>

      </div>

    }
  }
}

class DisputeInvestigationOutcome extends waffle.base.BaseComponent {
    render() {
        const transaction = Immutable.fromJS(this.props.parent);
        const disputeInvestigation = transaction.get('disputeInvestigation');
        // there is a possibility that there is no dispute investigation object, therefore a call to .get will fail. Null
        // does not have a `.get` attribute, therefore we default the outcome to null in such a case.
        const disputeInvestigationOutcome = disputeInvestigation ? disputeInvestigation.get('outcome') : null;

        // if there is disputeInvestigation, but there is no disputeInvestigationOutcome, then set the outcome to `Open/In Dispute`
        // this could be the case were the disputeInvestigation was created, but the outcome was not set
        return (
          <div>
            <span className="text-danger" style={{ textTransform: 'capitalize' }}>
              {
                (disputeInvestigation && !disputeInvestigationOutcome)
                ? "In Dispute"
                : disputeInvestigationOutcome
              }
            </span>
          </div>
        )
    }
}

module.exports = {
  TransactionFailureCategory,
  TransactionPayoutState,
  TransactionReconState,
  TransactionFailureInformation,
  ClearingInfo,
  OnlineInfo,
  TransactionLocation,
  PaymentDetails,
  ProcessingDetails,
  ReaderInfo,
  HardwareInfo,
  DisputeInvestigationOutcome
};
